import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { BLOCKED_PATH, NOTIFICATIONS_PATH, PROFILE_EDIT_PATH, activitiesPath } from '../app/paths';
import { Button } from '../components/ui/button';
import { ScreenMessage } from '../components/ui/screen-message';
import { useSession } from '../features/auth/session-gate';
import { LegalLink } from '../features/legal/legal-link';
import { useNotifications } from '../features/notifications/use-notifications';
import { readEnv } from '../lib/env';
import { openTelegramChat } from '../lib/telegram';
import { DeleteAccountSheet } from '../features/profile/delete-account-sheet';
import { ProfileCard } from '../features/profile/profile-card';
import { ReviewsSection } from '../features/reviews/reviews-section';
import { useProfile } from '../features/profile/use-profile';
import { ScreenSkeleton } from '../components/ui/skeleton';

/** S12 own profile (Профіль tab). Notification settings arrive with T-903. */
export function ProfileScreen() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { me } = useSession();
	const profile = useProfile(me.id);
	const [deleting, setDeleting] = useState(false);
	const unread = useNotifications().data?.pages[0]?.unreadCount ?? 0;
	const support = useMemo(() => readEnv().VITE_SUPPORT_TELEGRAM, []);

	if (profile.isPending) return <ScreenSkeleton kind="profile" />;
	if (profile.isError) {
		return (
			<ScreenMessage
				title={t('profile.title')}
				body={t('errors.generic_body')}
				action={{ label: t('errors.retry'), onClick: () => void profile.refetch() }}
			/>
		);
	}
	return (
		<div className="flex flex-col gap-6 px-4 py-6">
			<ProfileCard profile={profile.data} locale={me.locale} />
			<ReviewsSection userId={profile.data.id} locale={me.locale} />
			<div className="flex flex-col gap-2">
				<Button onClick={() => void navigate(PROFILE_EDIT_PATH)}>{t('profile.edit')}</Button>
				<Button variant="secondary" onClick={() => void navigate(activitiesPath('upcoming'))}>
					{t('profile.my_activities')}
				</Button>
				<Button variant="secondary" onClick={() => void navigate(activitiesPath('saved'))}>
					{t('profile.saved')}
				</Button>
				<Button variant="secondary" onClick={() => void navigate(NOTIFICATIONS_PATH)}>
					{unread > 0
						? t('notifications.open_unread', { count: unread })
						: t('notifications.title')}
				</Button>
				<Button variant="ghost" onClick={() => void navigate(BLOCKED_PATH)}>
					{t('profile.blocked')}
				</Button>
				<Button
					variant="ghost"
					onClick={() => {
						setDeleting(true);
					}}
				>
					{t('account.delete')}
				</Button>
			</div>
			{support !== undefined && (
				<Button
					variant="ghost"
					onClick={() => {
						openTelegramChat(support);
					}}
				>
					{t('profile.support')}
				</Button>
			)}
			<nav className="text-caption flex justify-center gap-4">
				<LegalLink doc="terms" locale={me.locale}>
					{t('legal.terms')}
				</LegalLink>
				<LegalLink doc="privacy" locale={me.locale}>
					{t('legal.privacy')}
				</LegalLink>
			</nav>
			{deleting && (
				<DeleteAccountSheet
					onClose={() => {
						setDeleting(false);
					}}
				/>
			)}
		</div>
	);
}
